// Detalhe do expediente já numerado — read-only por definição.
// Aqui não há "editar". Se o documento saiu errado, o caminho é cancelar com
// motivo e emitir um substituto: a série mantém o número queimado e a timeline
// mostra a ligação entre os dois.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, App as AntApp, Button, Descriptions, Divider, Drawer, Empty, Flex,
  Input, Space, Spin, Tag, Timeline, Typography,
} from 'antd';
import {
  FilePdfOutlined, StopOutlined, FileAddOutlined, SignatureOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { fmtDataBr } from '../../utils/dataFmt';
import {
  obterExpediente, cancelarExpediente, substituirExpediente, pdfExpediente,
  statusAssinatura, cancelarAssinatura, pdfAssinado, verificarAssinatura,
  retomarAssinatura,
  Expediente, Envolvido, EventoExpediente, STATUS_CFG, Envelope, ENVELOPE_CFG,
} from './api';
import ModalAssinatura from './ModalAssinatura';
import { avisoDoEnvio } from '../../components/assinatura/avisoEnvio';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const EVENTO_COR: Record<string, string> = {
  finalizado: 'green',
  cancelado: 'red',
  acesso_sigiloso: 'orange',
  substituido: 'purple',
};

export default function DrawerExpediente({ id, onClose, onMudou }: {
  id: string | null;
  onClose: () => void;
  onMudou: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const nav = useNavigate();

  const [exp, setExp] = useState<Expediente | null>(null);
  const [envolvidos, setEnvolvidos] = useState<Envolvido[]>([]);
  const [eventos, setEventos] = useState<EventoExpediente[]>([]);
  const [loading, setLoading] = useState(false);
  const [porVerTodos, setPorVerTodos] = useState(false);
  // ref, não state: o conteúdo do modal.confirm é montado UMA vez e a função
  // onOk fecha sobre o valor daquele instante — com state, o motivo digitado
  // chegaria sempre vazio no backend (que recusa com "mínimo 10 caracteres").
  const motivoRef = useRef('');
  const [envelope, setEnvelope] = useState<Envelope | null>(null);
  const [modalAssinar, setModalAssinar] = useState(false);
  const [verificando, setVerificando] = useState(false);

  const carregar = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await obterExpediente(id);
      setExp(d.expediente);
      setEnvolvidos(d.envolvidos || []);
      setEventos(d.eventos || []);
      setPorVerTodos(!!d.por_ver_todos);
      // Status vem do NOSSO banco (quem fala com a D4 Sign é o cron), então
      // dá pra ler junto sem gastar chamada externa.
      try { const a = await statusAssinatura(id); setEnvelope(a.envelope); }
      catch { setEnvelope(null); }
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [id, message]);

  useEffect(() => { if (id) carregar(); else { setExp(null); setPorVerTodos(false); } }, [id, carregar]);

  async function baixar() {
    if (!exp) return;
    try {
      const r = await pdfExpediente(exp.id);
      window.open(r.url, '_blank');
    } catch (e: any) {
      message.error('Falha ao gerar o PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  // O status se atualiza sozinho a cada 15 min; este botão é para quem acabou
  // de assinar e está olhando a tela. Compara o antes com o depois porque
  // "consultei e nada mudou" e "consultei e agora assinou" precisam de
  // respostas diferentes — sem isso o clique parece não ter feito nada.
  async function verificarAgora() {
    if (!exp || !envelope) return;
    setVerificando(true);
    const antesAssinados = envelope.signatarios.filter(s => s.status === 'assinado').length;
    const antesStatus = envelope.status;
    try {
      const r = await verificarAssinatura(exp.id);
      const novo = r.envelope;
      setEnvelope(novo);
      const agora = novo?.signatarios.filter(s => s.status === 'assinado').length ?? 0;
      if (novo?.status === 'assinado') {
        // O download do assinado pode ter falhado mesmo com todos assinando —
        // afirmar que está no acervo mandaria a pessoa procurar um arquivo que
        // não existe (e o botão de baixar nem aparece sem a chave).
        if (novo.pdf_assinado_s3_key) {
          message.success('Assinado! O documento assinado já está no acervo.');
        } else {
          message.warning('Assinado na D4 Sign, mas o PDF assinado ainda não chegou '
                        + 'ao acervo. Verifique de novo em alguns minutos.', 8);
        }
        carregar(); onMudou();          // status do documento e PDF assinado mudaram
      } else if (novo && novo.status !== antesStatus) {
        // Cancelamento pelo portal da D4 Sign entra por aqui: o banco mudou
        // (status do expediente + evento na timeline), então a tela toda
        // precisa recarregar, não só a etiqueta.
        message.info(`Status agora: ${ENVELOPE_CFG[novo.status]?.label || novo.status}`);
        carregar(); onMudou();
      } else if (agora > antesAssinados) {
        message.success(`${agora} de ${novo?.signatarios.length} assinaram agora.`);
      } else {
        message.info('Consultei a D4 Sign: ainda não há assinatura nova.');
      }
    } catch (e: any) {
      message.warning(e.response?.data?.error || e.message, 6);
    } finally { setVerificando(false); }
  }

  async function confirmarCancelamento() {
    if (!exp) return;
    try {
      await cancelarExpediente(exp.id, motivoRef.current);
      message.success(`${exp.codigo} cancelado`);
      motivoRef.current = '';
      carregar(); onMudou();
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      message.error(msg);
      throw new Error(msg);   // mantém o modal aberto pra corrigir o motivo
    }
  }

  // "Expediente" nos textos vira "documento" quando a área é jurídica — o
  // drawer é reusado pelo Jurídico (contrato/distrato) desde 2026-09-01.
  const rotuloDoc = exp?.area === 'juridico' ? 'documento' : 'expediente';

  function abrirCancelamento() {
    modal.confirm({
      title: `Cancelar ${exp?.codigo}?`,
      width: 520,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            O número <Text code>{exp?.codigo}</Text> continua ocupado — a série
            precisa se explicar, então ele não volta a ficar disponível.
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>Motivo (mínimo 10 caracteres):</Text>
          <TextArea rows={3} onChange={(e) => { motivoRef.current = e.target.value; }}
            placeholder="Ex.: CNPJ do destinatário estava incorreto; substituído pelo ofício seguinte." />
        </div>
      ),
      okText: `Cancelar ${rotuloDoc}`, okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: confirmarCancelamento,
    });
  }

  async function emitirSubstituto() {
    if (!exp) return;
    try {
      const r = await substituirExpediente(exp.id);
      message.success(`Rascunho criado em substituição a ${r.substitui}`);
      onClose();
      nav(`/administrativo/expedientes/${r.id}`);
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    }
  }

  return (
    <Drawer
      open={!!id} onClose={onClose} width="min(760px, 96vw)"
      title={exp ? (
        <Space>
          <Text strong style={{ fontFamily: 'monospace' }}>{exp.codigo}</Text>
          <Tag color={STATUS_CFG[exp.status]?.cor}>{STATUS_CFG[exp.status]?.label}</Tag>
        </Space>
      ) : 'Expediente'}
      extra={exp && (
        <Space>
          <Button icon={<FilePdfOutlined />} onClick={baixar}>PDF</Button>
          {/* `cancelado` também reabre o caminho: o próprio backend instrui
              "cancele o anterior antes de enviar de novo" — sem este botão o
              conselho era impossível de seguir, porque cancelar escondia
              qualquer forma de reenviar. */}
          {exp.status === 'finalizado' && (!envelope || envelope.status === 'cancelado') && (
            <Button type="primary" icon={<SignatureOutlined />} onClick={() => setModalAssinar(true)}>
              {envelope ? 'Assinar de novo' : 'Assinar'}
            </Button>
          )}
          {exp.status === 'finalizado' && (
            <Button danger icon={<StopOutlined />} onClick={abrirCancelamento}>Cancelar</Button>
          )}
          {exp.status === 'cancelado' && !exp.substituido_por_id && (
            <Button type="primary" icon={<FileAddOutlined />} onClick={emitirSubstituto}>
              Emitir substituto
            </Button>
          )}
        </Space>
      )}
    >
      <Spin spinning={loading}>
        {!exp ? <Empty /> : (
          <>
            {porVerTodos && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="Você está vendo um expediente do qual não participa"
                description="Este acesso ficou registrado na trilha do documento." />
            )}
            {exp.status === 'cancelado' && (
              <Alert type="error" showIcon style={{ marginBottom: 12 }}
                message={exp.area === 'juridico' ? 'Documento cancelado' : 'Expediente cancelado'}
                description={exp.cancelado_motivo} />
            )}

            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Tipo">{exp.tipo_nome}</Descriptions.Item>
              <Descriptions.Item label="Data">{fmtDataBr(exp.data_documento || '')}</Descriptions.Item>
              <Descriptions.Item label="Assunto">{exp.assunto || '—'}</Descriptions.Item>
              {exp.destinatario_nome && (
                <Descriptions.Item label="Destinatário">
                  {exp.destinatario_nome}
                  {exp.destinatario_cargo && <><br /><Text type="secondary">{exp.destinatario_cargo}</Text></>}
                  {exp.destinatario_org && <><br /><Text type="secondary">{exp.destinatario_org}</Text></>}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Elaborado por">{exp.criado_por || '—'}</Descriptions.Item>
              <Descriptions.Item label="Participantes">
                <Space wrap size={4}>
                  {envolvidos.map(e => (
                    <Tag key={`${e.usuario_email}-${e.papel}`}
                      color={e.papel === 'autor' ? 'gold' : e.papel === 'signatario' ? 'green' : 'default'}>
                      {e.nome_exibicao || e.usuario_email}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {/* Assinatura — o status vem do nosso banco, atualizado pelo cron.
                Não precisa deixar a tela aberta. */}
            {envelope && (
              <>
                <Divider titlePlacement="start" style={{ marginTop: 20 }}>Assinatura</Divider>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Space wrap>
                    <Tag color={ENVELOPE_CFG[envelope.status]?.cor}>
                      {ENVELOPE_CFG[envelope.status]?.label || envelope.status}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {envelope.signatarios.filter(s => s.status === 'assinado').length} de{' '}
                      {envelope.signatarios.length} assinaram
                      {envelope.workflow ? ' · em ordem' : ''}
                    </Text>
                  </Space>
                  {/* O motivo vem mastigado do servidor. Antes dizia só "veja
                      a trilha": em 30/09/2026 isso custou quatro tentativas
                      seguidas no mesmo ofício, cada uma consumindo a cota que
                      já estava estourada. */}
                  {(() => {
                    const aviso = avisoDoEnvio(envelope);
                    return aviso ? (
                      <Alert type={aviso.tipo} showIcon
                             message={aviso.titulo} description={aviso.descricao} />
                    ) : null;
                  })()}
                  {/* Assinado, sem o arquivo de volta: dizer que está a caminho
                      evita a leitura "a assinatura se perdeu". */}
                  {envelope.aguardando_arquivo && (
                    <Alert type="info" showIcon
                      message="Assinado — buscando o arquivo assinado"
                      description={
                        'A assinatura foi concluída, mas a D4 Sign ainda não entregou o PDF '
                        + 'assinado (costuma ser limite de operações por hora). O sistema tenta '
                        + 'sozinho de novo'
                        + (envelope.arquivo_previsto_em
                          ? ` a partir das ${new Date(envelope.arquivo_previsto_em)
                              .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                          : ' em até uma hora')
                        + ' — não é preciso assinar de novo.'} />
                  )}
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    {envelope.signatarios.map(s => (
                      <Flex key={s.email} justify="space-between" align="center">
                        <Space size={6}>
                          {envelope.workflow && <Text type="secondary" style={{ fontSize: 11 }}>{s.ordem}.</Text>}
                          <Text style={{ fontSize: 13 }}>{s.nome}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{s.email}</Text>
                        </Space>
                        <Tag color={s.status === 'assinado' ? 'green' : 'default'}>
                          {s.status === 'assinado' ? 'assinou' : 'pendente'}
                        </Tag>
                      </Flex>
                    ))}
                  </Space>
                  <Space>
                    {envelope.status === 'assinado' && envelope.pdf_assinado_s3_key && (
                      <Button size="small" type="primary" icon={<FilePdfOutlined />}
                        onClick={async () => {
                          try {
                            const r = await pdfAssinado(exp.id);
                            window.open(r.url, '_blank');
                          } catch (e: any) { message.error(e.response?.data?.error || e.message); }
                        }}>
                        Baixar assinado
                      </Button>
                    )}
                    {['aguardando_assinatura', 'processando_envio'].includes(envelope.status) && (
                      <Button size="small" icon={<ReloadOutlined />} loading={verificando}
                        onClick={verificarAgora}>
                        Verificar agora
                      </Button>
                    )}
                    {['aguardando_assinatura', 'processando_envio'].includes(envelope.status) && (
                      <Button size="small" danger onClick={async () => {
                        try {
                          await cancelarAssinatura(exp.id, 'Cancelado pelo usuário');
                          message.success('Envio cancelado');
                          carregar();
                        } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
                      }}>Cancelar assinatura</Button>
                    )}
                    {/* 🚨 O botão segue a régua, não o status. Com o documento já
                        no cofre da D4 Sign, "Tentar de novo" subiria uma SEGUNDA
                        via assinável — o servidor recusa com 409, e a tela não
                        pode oferecer o clique que ela sabe que vai falhar. */}
                    {avisoDoEnvio(envelope)?.acao === 'reenviar' && (
                      <Button size="small" type="primary" onClick={() => setModalAssinar(true)}>
                        Tentar de novo
                      </Button>
                    )}
                    {avisoDoEnvio(envelope)?.acao === 'retomar' && (
                      <Button size="small" type="primary" onClick={async () => {
                        try {
                          await retomarAssinatura(exp.id);
                          message.success('Retomando o envio — os signatários serão avisados');
                          carregar();
                        } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
                      }}>Retomar envio</Button>
                    )}
                    {envelope.status === 'erro_envio' && envelope.retentativa?.no_cofre && (
                      <Button size="small" danger onClick={async () => {
                        try {
                          await cancelarAssinatura(exp.id, 'Via que ficou no cofre após falha no envio');
                          message.success('Via cancelada na D4 Sign');
                          carregar();
                        } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
                      }}>Cancelar a via do cofre</Button>
                    )}
                  </Space>
                </Space>
              </>
            )}

            {/* antd desta versão: `orientation` é horizontal/vertical; quem
                alinha o título é `titlePlacement`. */}
            <Divider titlePlacement="start" style={{ marginTop: 20 }}>Documento</Divider>
            <div style={{ background: '#fff', padding: 20, border: '1px solid #eee',
                          fontFamily: '"Times New Roman", Times, serif', fontSize: 14,
                          lineHeight: 1.5, textAlign: 'justify' }}
              dangerouslySetInnerHTML={{ __html: exp.corpo_final_html || exp.corpo_html || '' }} />

            <Divider titlePlacement="start" style={{ marginTop: 20 }}>Histórico</Divider>
            <Timeline
              items={eventos.map(ev => ({
                color: EVENTO_COR[ev.tipo] || 'blue',
                children: (
                  <div>
                    <Text style={{ fontSize: 13 }}>{ev.descricao}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {ev.usuario_email || 'sistema'} · {new Date(ev.criado_em).toLocaleString('pt-BR')}
                      </Text>
                    </div>
                  </div>
                ),
              }))}
            />
          </>
        )}
      </Spin>

      <ModalAssinatura
        open={modalAssinar}
        expedienteId={exp?.id || null}
        codigo={exp?.codigo || null}
        envolvidos={envolvidos}
        area={exp?.area}
        envelopeAnterior={envelope}
        onClose={() => setModalAssinar(false)}
        onEnviado={carregar}
      />
    </Drawer>
  );
}
